import { useMutation, useQueryClient } from '@tanstack/react-query'
import debounce from 'lodash.debounce'

import { useToast } from '@/src/general/components/ToastContext/ToastContext'
import { ICON } from '@/src/general/enums/icons'
import { useTranslate } from '@/src/lang/useTranslate'

import { publicationApi } from '../api/publicationApi'
import { publicationOptions } from './options/publicationOptions'

export const useToggleFavoritePublication = () => {
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  const { t } = useTranslate()

  const mutation = useMutation({
    mutationFn: async ({ publicationId, action }: { publicationId: number; action: 'add' | 'remove' }) => {
      if (action === 'add') {
        await publicationApi.markAsFavorite(publicationId)
        return
      }
      await publicationApi.unmarkAsFavorite(publicationId)
    },
    onMutate: ({ publicationId, action }) => {
      queryClient.setQueryData(
        publicationOptions(publicationId).queryKey,
        (oldData) => oldData && { ...oldData, isFavorite: action === 'add' },
      )
    },
    onSuccess: (_, { publicationId, action }) => {
      addToast({
        message: t(action === 'add' ? 'publication.favorite.success' : 'publication.favorite.removed'),
        colors: 'success',
        iconName: action === 'add' ? ICON.BOOKMARK_FILL : ICON.BOOKMARK_X,
      })
      queryClient.invalidateQueries(publicationOptions(publicationId))
    },
    onError: (_, { publicationId, action }) => {
      queryClient.setQueryData(
        publicationOptions(publicationId).queryKey,
        (oldData) => oldData && { ...oldData, isFavorite: action === 'remove' },
      )
      queryClient.invalidateQueries({ queryKey: publicationOptions(publicationId).queryKey })
    },
  })

  return { ...mutation, mutate: debounce(mutation.mutate, 350, { trailing: true, leading: true }) }
}
